import { useCallback } from "react";
import {
  arrayUnion,
  doc,
  runTransaction,
  serverTimestamp,
  type Firestore,
  type UpdateData,
} from "firebase/firestore";
import type { BaseItem } from "../App";

type Params = {
  db: Firestore;
  currentUserString: () => string | null;
  toMillis: (v: any) => number | null;
  LOCK_STALE_MS: number;
};

export function useInvoiceToggle({ db, currentUserString, toMillis, LOCK_STALE_MS }: Params) {
  const setMarkedForInvoice = useCallback(async (itemId: string, checked: boolean): Promise<void> => {
    const user = currentUserString();
    const nowIso = new Date().toISOString();
    const ref = doc(db, "itInventory", itemId);

    try {
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(ref);
        if (!snap.exists()) throw new Error("Posten finns inte längre.");
        const cur = snap.data() as any;

        if (cur.invoiceReportId) throw new Error("Kan inte ändra — posten är fakturerad och permalåst.");

        const heldByOther = !!cur.lockedBy && cur.lockedBy !== user;
        const lockedAtMs = toMillis(cur.lockedAt);
        const isStale = lockedAtMs === null ? true : (Date.now() - lockedAtMs) > LOCK_STALE_MS;
        if (heldByOther && !isStale) {
          throw new Error(`Kan inte ändra — posten redigeras av ${cur.lockedBy}.`);
        }

        if (checked === true) {
          if (!cur.completed) throw new Error("Objektet måste vara färdigmarkerat innan det kan markeras för fakturering.");
          tx.update(ref, {
            markedForInvoice: true,
            deletePending: false,
            deleteMarkedBy: null,
            deleteMarkedAt: null,
            auditLog: arrayUnion({
              action: "marked_for_invoice",
              by: user,
              at: nowIso,
            }),
          } as UpdateData<BaseItem>);
        } else {
          tx.update(ref, {
            markedForInvoice: false,
            auditLog: arrayUnion({
              action: "unmarked_for_invoice",
              by: user,
              at: nowIso,
            }),
          } as UpdateData<BaseItem>);
        }
      });
    } catch (err: any) {
      console.error("Kunde inte uppdatera markedForInvoice", err);
      alert(err?.message || "Kunde inte uppdatera markeringen. Försök igen.");
      throw err;
    }
  }, [LOCK_STALE_MS, currentUserString, db, toMillis]);

  return { setMarkedForInvoice };
}
